<template>
    <v-form
        ref="form">
        <v-row>
            <v-col cols="12" md="3">
                <v-text-field
                    label="Code"
                    :value="form.code"
                    outlined
                    dense
                    hide-details="auto"
                    disabled
                    background-color="disabled">
                </v-text-field>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="3">
                <PDatePicker
                    label="Date"
                    v-model="form.date"
                    outlined
                    hide
                    :rules="[isRequired]"
                    />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="6" >
                <v-autocomplete
                    label="Supplier"
                    @update:search-input="searchSuppliers"
                    @change="handleSupplierChange"
                    v-model="form.supplier_id"
                    :items="suppliers"
                    :loading="suppliersLoading"
                    item-text="display_name"
                    :rules="[isRequired]"
                    item-value="id"
                    outlined
                    dense
                    hide-details="auto">
                </v-autocomplete>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="3" >
                <v-autocomplete
                    label="Mode"
                    v-model="form.mode"
                    :rules="[isRequired]"
                    :items="paymentModes"
                    :loading="paymentModesLoading"
                    outlined
                    @change="handleModeChange"
                    dense
                    hide-details="auto">
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" v-if="showCheques">
                <v-btn depressed block color="primary" @click="addCheque">add cheque</v-btn>
            </v-col>
            <v-col cols="12" md="3" v-if="isBankTransferType">
                <v-text-field
                    label="Reference"
                    :value="form.reference_number"
                    outlined
                    :rules="[isRequired]"
                    dense
                    hide-details="auto"
                    >
                </v-text-field>
            </v-col>
        </v-row>
        <v-row>     
            <v-col cols="12" md="3" >
                <v-autocomplete
                    label="Paid From"
                    v-model="form.ledger_account_id"
                    :items="paymentAccounts"
                    item-text="name"
                    item-value="id"
                    :rules="[isRequired]"
                    outlined
                    dense
                    hide-details="auto">
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" >
                <v-text-field
                    label="Paid Amount"
                    v-model="form.paid_amount"
                    outlined
                    dense
                    :rules="[isRequired, isNumeric]"
                    :disabled="isChequeType"
                    :prefix="selectedCurrency ? selectedCurrency.abbreviation : null"
                    hide-details="auto">
                </v-text-field>
            </v-col>
            <v-col cols="12" md="3" >
                <v-text-field
                    label="Base Currency"
                    :value="baseAmount"
                    outlined
                    dense
                    :rules="[isRequired, isNumeric]"
                    disabled
                    background-color="disabled"
                    :prefix="baseCurrency.abbreviation"
                    hide-details="auto">
                </v-text-field>
            </v-col>
        </v-row>
        <v-row >
            
            <v-col cols="12" md="3">
                <v-autocomplete
                    label="Paid To"
                    v-model="form.account_id"
                    :rules="[isRequired]"
                    :items="supplierAccounts"
                    item-text="display_name"
                    item-value="id"
                    outlined
                    dense
                    hide-details="auto">
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" >
                <v-text-field
                    label="Supplier Account Currency"
                    :value="supplierAccountAmount"
                    outlined
                    dense
                    :rules="[isRequired, isNumeric]"
                    disabled
                    background-color="disabled"
                    :prefix="supplierAccountCurrency ? supplierAccountCurrency.abbreviation : null"
                    hide-details="auto">
                </v-text-field>
            </v-col>
            
        </v-row>
        <v-row v-if="showCheques">
            <v-col>
                <PurchasePaymentFormCheque
                    v-for="(d, i) in form.cheques"
                    :key="d.key"
                    :index="i"
                    :currency="currency"
                    :bankAccounts="BankAccountsByCurrency"
                    :exchangeRate="paidAmountExchangeRate"
                    v-model="form.cheques[i]"
                    @input="handleChequesInput"
                    @cheque:delete="deleteCheque"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="6">
                <v-textarea
                    label="Narration"
                    v-model="form.narration"
                    dense
                    :rules="[isRequired]"
                    hide-details="auto"
                    outlined>
                </v-textarea>
            </v-col>
        </v-row>
        <ExchangeRateIndex
            :value="exchangeRateIndexShown"
            @input="$emit('update:exchange-rate-index-shown', $event)"
            v-if="exchangeRateIndexShown"
            :exchange-rates.sync="exchangeRates"
            />
    </v-form>
</template>

<script>
import PDatePicker from '@/components/common/PDatePicker';
import PurchasePaymentFormCheque from '@/components/purchase/PurchasePaymentFormCheque';
import { mapActions, mapGetters } from 'vuex';
import { getTotal } from './../../utils/accounting';
import { isRequired, isNumeric } from './../../utils/validations';
import { searchExchangeRates } from "./../../services/ledger/exchangeRates";
import ExchangeRateIndex from './../accounting/ExchangeRateIndex.vue';

export default {
    name: "PurchasePaymentForm",
    components: {
        PDatePicker,
        PurchasePaymentFormCheque,
        ExchangeRateIndex
    },
    props: {
        value: Object,
        exchangeRateIndexShown: Boolean,
        editMode: Boolean,
    },
    data() {
        return {
            form: {
                id: null,
                code: null,
                date: null,
                mode: null,
                supplier_id: null,
                account_id: null,
                ledger_account_id: null,
                paid_amount: null,
                base_amount: null,
                amount: null,
                narration: null,
                cheques: []
            },
            cheque: {
                id: null,
                date: null,
                number: null,
                ledger_account_id: null,
                amount: null
            },
            MODES: {
                CASH: "CASH",
                CHEQUE: "CHEQUE",
                BANK_TRANSFER: "TRANSFER"
            },
            baseCurrency: {
                id: 1,
                name: "Sudanese Pounds",
                abbreviation: "SDG"
            },
            exchangeRates: [],
        }
    },
    computed: {
        ...mapGetters({
            paymentModes: "purchase/paymentMode/list",
            paymentModesLoading: "purchase/paymentMode/loading",
            suppliers: "purchase/supplier/list",
            suppliersLoading: "purchase/supplier/loading",
        }),
        bankAccounts() {
            let bankMode = this.paymentModes.find(mode => mode.value == this.MODES.BANK_TRANSFER);
            if(!bankMode) return [];
            return bankMode.accounts;
        },
        selectedSupplier() {
            return this.suppliers.find(supplier => supplier.id == this.form.supplier_id);
        },
        supplierAccounts() {
            if(!this.selectedSupplier) return [];
            if(!this.selectedSupplier.accounts) return [];
            if(!this.selectedSupplier.accounts.length) return [];
            return this.selectedSupplier.accounts;
        },
        selectedPaymentMode() {
            return this.paymentModes.find(paymentType => paymentType.value == this.form.mode);
        },
        paymentAccounts() {
            return this.selectedPaymentMode ? this.selectedPaymentMode.accounts : [];
        },
        selectedLedgerAccount() {
            return this.paymentAccounts.find(account => account.id == this.form.ledger_account_id);
        },
        currency() {
            if(!this.selectedLedgerAccount) return null;
            return this.selectedLedgerAccount.currency;
        },
        selectedCurrency() {
            if(!this.selectedLedgerAccount) return null;
            return this.selectedLedgerAccount.currency;
        },
        showCheques() {
            return this.selectedPaymentMode && this.selectedPaymentMode.value == this.MODES.CHEQUE;
        },
        BankAccountsByCurrency() {
            if(!this.bankAccounts.length) return [];
            if(!this.selectedCurrency) return [];

            return this.bankAccounts.filter(account => {
                if(!account.currency) return false;
                return account.currency.id == this.selectedCurrency.id
            })
        },
        isChequeType() {
            return this.selectedPaymentMode ? this.selectedPaymentMode.value == this.MODES.CHEQUE : false
        },
        isBankTransferType() {
            return this.selectedPaymentMode ? this.selectedPaymentMode.value == this.MODES.BANK_TRANSFER : false
        },
        supplierAccount() {
            return this.supplierAccounts.find(account => this.form.account_id == account.id);
        },
        supplierAccountCurrency() {
            if(!this.supplierAccount) return null;
            return this.supplierAccount.currency;
        },
        paidAmountExchangeRate() {
            if(!this.selectedLedgerAccount) return null;
            return this.exchangeRates.find(rate => rate.currency_id == this.selectedLedgerAccount.currency.id);
        },
        baseAmount() {
            if(!this.paidAmountExchangeRate) return 0;
            return this.form.paid_amount * this.paidAmountExchangeRate.customRate;
        },
        supplierAccountCurrencyExchangeRate() {
            if(!this.supplierAccountCurrency) return null;
            return this.exchangeRates.find(rate => rate.currency_id == this.supplierAccountCurrency.id);    
        },
        supplierAccountAmount() {
            if(!this.supplierAccountCurrencyExchangeRate) return 0;
            return this.baseAmount / this.supplierAccountCurrencyExchangeRate.customRate;
        }
    },
    watch: {
        supplierQuery(query) {
            this.updateSupplierQurey(query);
        },
        form: {
            deep: true,
            handler(form) {
                this.$emit("input", {
                    ...form,
                    cheques: [...form.cheques]
                    
                });
            }
        },
        baseAmount(amount) {
            this.form.base_amount = amount;
        },
        supplierAccountAmount(amount) {
            this.form.amount = amount;
        },
        // watch for changes in date and then refetch the exchange rates
        // according to the new date. this should only happen when creating
        // new payments. modifying existing payment should not have such effect
        "form.date": {
            handler(date) {
                if(!this.editMode) {
                    this.fetchExchangeRates(date);
                }
            }
        },
    },
    methods: {
        isRequired, isNumeric,
        ...mapActions({
            fetchPaymentModes: "purchase/paymentMode/index",
            searchSuppliers: "purchase/supplier/search",
            // fetchPaymentBankAccounts: "purchase/payment/getBankAccounts"
        }),
        addCheque() {
            this.form.cheques.push({
                ...this.cheque,
                key: Math.random()
            })
        },
        deleteCheque(i) {
            this.form.cheques.splice(i, 1);
            this.form.amount = getTotal(this.form.cheques, "amount");
        },
        validate() {
            return this.$refs.form.validate();
        },
        handleChequesInput() {
            // Set the amount as the total cheques amount
            this.form.paid_amount = getTotal(this.form.cheques, "amount");
        },
        freshValue() {
            this.form = {...this.value}
        },
        handleModeChange(mode) {
            this.form.ledger_account_id = null;
            this.form.cheques = [];
            if(mode.value == this.MODES.CHEQUE) {
                this.form.amount = 0;
                this.addCheque();
            }
        },
        handleSupplierChange() {
            this.form.account_id = null;
        },
        async fetchExchangeRates(date) {
            let response = await searchExchangeRates({date});
            this.exchangeRates = response.data.rates.map(each => {
                return {
                    ...each,
                    customRate: each.rate
                }
            });
        },
        /**
         * evaluateCustomRates fetchs the custom rate from api
         * and override the custom rate 
         */
        evaluateCustomRates() {
            // revaluate the custom rate for paid amount
            let baseAmount = this.form.base_amount;

            // the amount in supplier account currency
            let accountAmount = this.form.amount;
            let accountAmountCurrency = this.form.account.currency;

            // the amount in paid from account currency
            let paidAmount = this.form.paid_amount;
            let paidAmountCurrency = this.form.ledger_account.currency;

            // supplier account amount exchange rate
            let accountER = baseAmount / accountAmount;
            // payment amount exchange rate
            let paidER = baseAmount / paidAmount;

            (async function() {
                await this.fetchExchangeRates(this.form.date)
    
                let i = this.exchangeRates.findIndex(each => each.currency_id == accountAmountCurrency.id)
                this.exchangeRates[i].customRate = accountER;
    
                let j = this.exchangeRates.findIndex(each => each.currency_id == paidAmountCurrency.id)
                this.exchangeRates[j].customRate = paidER;
            }).bind(this)()
        },
        /**
         * deepCloneValue takes the value from the props and apply
         * deep cloning to the object and nested objects/arrays
         */
        deepCloneValue(value) {
            // clone the form level attributes
            let clone = {...value}
            if(value.cheques) {
                /// clone cheques arrays
                clone = {...clone, cheques: [...clone.cheques]};
            }
            return clone;
        }
    },
    mounted() {
        this.fetchPaymentModes();
        // Deep clone the value prop into the form.
        if(this.value) {
            this.form = this.deepCloneValue(this.value)
            this.evaluateCustomRates();            
        }
    }
}
</script>

<style>

</style>